import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AttributeRuntimeOptionDialog } from "@/components/catalog/AttributeRuntimeOptionDialog";
import { attributeScalarToOptionString } from "@/lib/catalog/attributeScalarString";
import type { Attribute, AttributeOption, AttributeValue } from "@/lib/catalog/types";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { useState } from "react";

function sortOptions(options: AttributeOption[]): AttributeOption[] {
  return [...options].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

type Props = {
  attribute: Attribute;
  value: AttributeValue | undefined;
  onChange: (value: AttributeValue) => void;
  onOptionsUpdated?: (attributeId: string, options: AttributeOption[]) => void;
  triggerClassName?: string;
  placeholder?: string;
  runtimeOpen?: boolean;
  onRuntimeOpenChange?: (open: boolean) => void;
  disabled?: boolean;
};

export function AttributeSelectControl({
  attribute,
  value,
  onChange,
  onOptionsUpdated,
  triggerClassName,
  placeholder,
  runtimeOpen,
  onRuntimeOpenChange,
  disabled = false,
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogOpen = runtimeOpen ?? internalOpen;
  const setDialogOpen = onRuntimeOpenChange ?? setInternalOpen;
  const current = attributeScalarToOptionString(value);
  const options = sortOptions(attribute.options ?? []);
  const canRuntimeAdd = attribute.type === "select" && Boolean(attribute.allowRuntimeAdd);

  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);

  const selected = options.find((opt) => String(opt.value ?? "") === current);
  const displayLabel =
    selected?.label ?? (current ? current : (placeholder ?? `Select ${attribute.name.toLowerCase()}`));

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            onKeyDown={onTriggerKeyDown}
            className={cn(
              "glass-input h-10 w-full justify-between font-normal text-sm px-3",
              !selected && !current && "text-muted-foreground",
              triggerClassName,
            )}
          >
            <span className="truncate">{displayLabel}</span>
            <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-[200px] p-0 z-[100]"
          align="start"
          sideOffset={4}
        >
          <Command shouldFilter>
            <CommandInput
              {...commandInputProps}
              placeholder={`Search ${attribute.name.toLowerCase()}...`}
              className="h-9 text-xs"
            />
            <CommandList className="max-h-[220px] overflow-y-auto">
              <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
                No option found.
              </CommandEmpty>
              <CommandGroup>
                {!attribute.isRequired && current ? (
                  <CommandItem
                    value="__clear_selection__"
                    onSelect={() => {
                      onChange("");
                      setOpen(false);
                    }}
                    className="text-xs text-muted-foreground italic cursor-pointer"
                  >
                    Clear selection
                  </CommandItem>
                ) : null}
                {options.map((opt) => {
                  const optValue = String(opt.value ?? "");
                  if (!optValue) return null;
                  const isSelected = optValue === current;
                  return (
                    <CommandItem
                      key={opt.id}
                      value={`${opt.label} ${optValue}`}
                      onSelect={() => {
                        onChange(optValue);
                        setOpen(false);
                      }}
                      className="text-xs flex items-center justify-between cursor-pointer"
                    >
                      <span className="truncate">{opt.label}</span>
                      <Check
                        className={cn(
                          "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
            {canRuntimeAdd ? (
              <div className="border-t border-border/40 p-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs text-primary hover:bg-primary/10 h-8 gap-1.5"
                  onClick={() => {
                    setOpen(false);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add new option...
                </Button>
              </div>
            ) : null}
          </Command>
        </PopoverContent>
      </Popover>

      {canRuntimeAdd ? (
        <AttributeRuntimeOptionDialog
          attribute={attribute}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={(nextOptions, selectedValue) => {
            onOptionsUpdated?.(attribute.id, nextOptions);
            onChange(selectedValue);
          }}
        />
      ) : null}
    </>
  );
}

export function AttributeSelectRuntimeAddButton({
  attribute,
  onClick,
}: {
  attribute: Attribute;
  onClick: () => void;
}) {
  if (attribute.type !== "select" || !attribute.allowRuntimeAdd) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-4 w-7 shrink-0 text-primary hover:bg-primary/10"
      title={`Add ${attribute.name} option`}
      onClick={onClick}
    >
      <Plus className="h-4 w-4" />
    </Button>
  );
}
